/**
 * useFileUpload - Hook for handling file uploads to S3
 *
 * Handles:
 * - Multiple file upload with progress
 * - File validation
 * - S3 upload via AWS Amplify
 */

import { useState, useCallback } from 'react';
import { Auth, Storage } from 'aws-amplify';
import Cookies from 'universal-cookie';
import { FILEUPLOAD_S3, FILEUPLOAD_S3_LEVEL, SetS3Config } from 'config/amplify_config';
import { uuidv4 } from 'common/uuidGenerator';
import { validateUpload } from '../services/messageService';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { MAX_FILE_ATTACHMENTS } from '../constants';
import { useUserDetailsContext } from 'context/userDetailsContext';

const cookie = new Cookies();

/**
 * @returns {{ uploadFiles, uploadProgress, isUploading, addFiles, removeFile, files, clearFiles }}
 */
export function useFileUpload() {
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const { addToast } = useToast();
  const { userDetails } = useUserDetailsContext();

  const addFiles = useCallback(
    (newFiles) => {
      const totalFiles = [...files, ...newFiles];
      // if (totalFiles.length > MAX_FILE_ATTACHMENTS) {
      //   addToast(
      //     false,
      //     toastConstant.toasterType.WARN,
      //     toastConstant.toasterType.WARNING,
      //     toastConstant.message.FILE_UPLOAD_LIMIT_EXCEEDED
      //   );
      //   return;
      // }
      setFiles(totalFiles);
    },
    [files, addToast]
  );

  const removeFile = useCallback((filePath) => {
    setFiles((prev) => prev.filter((f) => f.path !== filePath));
  }, []);

  const clearFiles = useCallback(() => {
    setFiles([]);
    setUploadProgress(0);
  }, []);

  /**
   * Upload all selected files to S3
   * @param {string} caseId - Case ID for file path
   * @returns {Promise<Array|null>} Uploaded attachment objects or null on validation failure
   */
  const uploadFiles = useCallback(
    async (caseId = '') => {
      if (!files || files.length === 0) return [];

      const filesData = Object.values(files);
      const totalSize = filesData.reduce((acc, f) => acc + f.size, 0);

      setIsUploading(true);
      // Validate upload size
      try {
        await validateUpload(totalSize);
      } catch (err) {
        const errorMsg = err?.response?.data?.message || err?.response?.data || err?.message || 'File validation failed';
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, errorMsg);
        setIsUploading(false);
        return null;
      }

      setUploadProgress(1);

      const attachments = [];
      let totalUploaded = 0;
      const perFileUploaded = filesData.map(() => 0);

      try {
        const tenantId = cookie.get('tenantId') || '';
        const moduleName = 'messages';
        const userId = userDetails?.firm_user_id;

        for (let i = 0; i < filesData.length; i++) {
          const element = filesData[i];
          SetS3Config(FILEUPLOAD_S3, FILEUPLOAD_S3_LEVEL);
          const user = await Auth.currentAuthenticatedUser();

          const response = await Storage.put(
            `${moduleName}/${tenantId}/${userId}/${caseId ? caseId + '/' : ''}${uuidv4()}#$#${encodeURIComponent(element.name)}`,
            element,
            {
              contentType: element.type,
              progressCallback: (progress) => {
                const delta = progress.loaded - perFileUploaded[i];
                if (delta > 0) {
                  perFileUploaded[i] = progress.loaded;
                  totalUploaded += delta;
                  const percent = Math.min(Math.floor((totalUploaded / totalSize) * 100), 100);
                  setUploadProgress(percent);
                }
              },
              useAccelerateEndpoint: true,
            }
          );

          if (response.key) {
            attachments.push({
              file_name: element.name,
              display_name: element.name,
              size: element.size.toString(),
              file_path: 'public/' + response.key,
              file_type: element.type.toString(),
              created_by: user.attributes,
            });
          }
        }

        setUploadProgress(100);
        return attachments;
      } catch (err) {
        console.error('File upload error:', err);
        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, 'File upload failed');
        return attachments;
      } finally {
        setIsUploading(false);
      }
    },
    [files, addToast, userDetails]
  );

  return {
    files,
    addFiles,
    removeFile,
    clearFiles,
    uploadFiles,
    uploadProgress,
    isUploading,
  };
}
